import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const NotificationPrompt = ({ userEmail = null, userName = null }) => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    // Check if notifications are supported
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    // Check current permission
    setPermission(Notification.permission);

    // Check if already subscribed
    if (Notification.permission === 'granted') {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (e) {
        console.error('Error checking subscription:', e);
      }
    }

    // Show prompt if not granted and not dismissed before
    const wasDismissed = localStorage.getItem('notificationPromptDismissed');
    if (Notification.permission === 'default' && !wasDismissed) {
      // Delay showing prompt for better UX
      setTimeout(() => setShowPrompt(true), 3000);
    }
  };

  const subscribeToPush = async () => {
    setLoading(true);

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const { data: vapidData } = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidData.publicKey;

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const subscriptionJSON = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys,
        user_email: userEmail,
        user_name: userName
      });

      setIsSubscribed(true);
      setShowPrompt(false);
      toast.success('Notifications enabled! You\'ll receive deal alerts and updates.');

    } catch (error) {
      console.error('Error subscribing to push:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to enable notifications');
      }
    } finally {
      setLoading(false);
    }
  };

  const requestPermissionAndSubscribe = async () => {
    setLoading(true);

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        await subscribeToPush();
      } else {
        toast.error('Notifications were blocked. Enable them in browser settings.');
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Failed to request notification permission');
    } finally {
      setLoading(false);
    }
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('notificationPromptDismissed', 'true');
  };

  const unsubscribe = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: subscription.endpoint
        });
      }
      
      setIsSubscribed(false);
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to disable notifications');
    }
  };

  // Don't render anything if notifications aren't supported
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  // Floating prompt for new users
  if (showPrompt && !dismissed) {
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-slide-up">
        <div className="bg-gradient-to-br from-purple-900 to-indigo-900 border border-purple-500/30 rounded-xl p-4 shadow-2xl shadow-purple-500/20">
          <button 
            onClick={dismissPrompt}
            className="absolute top-2 right-2 text-gray-400 hover:text-white"
          >
            <X size={16} />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Get Deal Alerts!</h4>
              <p className="text-gray-300 text-sm mb-3">
                Be the first to know about last-minute studio deals and exclusive discounts.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={requestPermissionAndSubscribe}
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Bell size={14} />
                  )}
                  Enable
                </button>
                <button
                  onClick={dismissPrompt}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-semibold transition-all"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Separate component for settings/toggle
export const NotificationToggle = ({ userEmail = null, userName = null }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        setSupported(false);
        return;
      }

      if (Notification.permission === 'granted') {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch (e) {
          console.error('Error checking subscription:', e);
        }
      }
    };
    
    checkSubscriptionStatus();
  }, []);

  const toggle = async () => {
    if (isSubscribed) {
      // Unsubscribe
      setLoading(true);
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          await axios.post(`${API}/push/unsubscribe`, { endpoint: subscription.endpoint });
        }
        setIsSubscribed(false);
        toast.success('Notifications disabled');
      } catch (e) {
        toast.error('Failed to disable notifications');
      }
      setLoading(false);
    } else {
      // Subscribe
      setLoading(true);
      try {
        const result = await Notification.requestPermission();
        if (result === 'granted') {
          const registration = await navigator.serviceWorker.register('/push-sw.js');
          await navigator.serviceWorker.ready;
          
          const { data: vapidData } = await axios.get(`${API}/push/vapid-public-key`);
          
          const urlBase64ToUint8Array = (base64String) => {
            const padding = '='.repeat((4 - base64String.length % 4) % 4);
            const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
            const rawData = window.atob(base64);
            const outputArray = new Uint8Array(rawData.length);
            for (let i = 0; i < rawData.length; ++i) {
              outputArray[i] = rawData.charCodeAt(i);
            }
            return outputArray;
          };

          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
          });

          const subscriptionJSON = subscription.toJSON();
          await axios.post(`${API}/push/subscribe`, {
            endpoint: subscriptionJSON.endpoint,
            keys: subscriptionJSON.keys,
            user_email: userEmail,
            user_name: userName
          });

          setIsSubscribed(true);
          toast.success('Notifications enabled!');
        } else {
          toast.error('Please allow notifications in browser settings');
        }
      } catch (e) {
        console.error('Subscribe error:', e);
        toast.error('Failed to enable notifications');
      }
      setLoading(false);
    }
  };

  if (!supported) {
    return null;
  }

  return (
    <button
      onClick={toggle}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
        isSubscribed
          ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
          : 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
      } disabled:opacity-50`}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
      ) : isSubscribed ? (
        <Check size={16} />
      ) : (
        <Bell size={16} />
      )}
      {isSubscribed ? 'Notifications On' : 'Enable Notifications'}
    </button>
  );
};

export default NotificationPrompt;
